import React, { useState } from 'react'
import FormSection from '../components/FormSection'
import OperationsAgendaReport from '../components/OperationsAgendaReport'

const MYLAR_LAMBDA_URL = import.meta.env.VITE_MYLAR_LAMBDA_URL || 'https://u6c2mrfjbgggpojbpjb33gabiq0ftqds.lambda-url.us-east-2.on.aws/'
const MYLAR_PORTAL_PUBLISH_URL = import.meta.env.VITE_MYLAR_PORTAL_PUBLISH_URL || ''
const OPS_PROGRESS_REPORT_URL = import.meta.env.VITE_OPS_PROGRESS_REPORT_URL || ''
const MONTHLY_BRIEFING_URL = 'https://economicdashboard.netlify.app/'

const Reports = () => {
  const [generating, setGenerating] = useState(false)
  const [generatingOpsProgress, setGeneratingOpsProgress] = useState(false)
  const [publishingMylarPortal, setPublishingMylarPortal] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const generateMylarReport = async () => {
    setGenerating(true)
    setError('')
    setSuccess('')

    try {
      const response = await fetch(MYLAR_LAMBDA_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: 'Sales Transactions & Construction Sequence Summary',
          includeCharts: true
        })
      })

      if (!response.ok) {
        throw new Error(`Report generation failed: ${response.status}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `mylar-report-${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      setSuccess('Mylar report generated and downloaded successfully')
    } catch (err) {
      setError(err.message || 'Failed to generate report')
    } finally {
      setGenerating(false)
    }
  }

  const generateOpsProgressReport = async () => {
    setGeneratingOpsProgress(true)
    setError('')
    setSuccess('')

    try {
      if (!OPS_PROGRESS_REPORT_URL) {
        throw new Error('OPS progress report URL not configured')
      }

      const response = await fetch(`${OPS_PROGRESS_REPORT_URL}?project_id=Aria`, {
        method: 'GET'
      })

      if (!response.ok) {
        throw new Error(`Report generation failed: ${response.status}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ops-progress-${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      setSuccess('OPS progress report generated and downloaded successfully')
    } catch (err) {
      setError(err.message || 'Failed to generate report')
    } finally {
      setGeneratingOpsProgress(false)
    }
  }

  const publishMylarPortal = async () => {
    setPublishingMylarPortal(true)
    setError('')
    setSuccess('')

    try {
      if (!MYLAR_PORTAL_PUBLISH_URL) {
        throw new Error('Mylar portal publisher URL not configured')
      }
      const response = await fetch(MYLAR_PORTAL_PUBLISH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'reports-page' })
      })
      if (!response.ok) {
        throw new Error(`Portal publish failed: ${response.status}`)
      }
      const payload = await response.json()
      const portalUrl = payload?.secure_portal_url || ''
      setSuccess(portalUrl
        ? `Secure Mylar portal published: ${portalUrl}`
        : 'Secure Mylar portal published successfully')
    } catch (err) {
      setError(err.message || 'Failed to publish secure Mylar portal')
    } finally {
      setPublishingMylarPortal(false)
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>Reports</h2>
        <p>Generate PDF reports for construction and sales tracking</p>
      </div>

      <FormSection title="Mylar Report">
        <p>
          The Mylar report combines construction milestones, sales status, and transaction details
          from all data sources into a comprehensive PDF report.
        </p>

        <div style={{ marginBottom: 16 }}>
          <h4>Report Contents:</h4>
          <ul>
            <li>Sales transactions by project with status colors</li>
            <li>Construction milestone snapshot mapped from Daily Checklist tasks</li>
            <li>Buyer information and contract details</li>
            <li>Milestone code and latest completed milestone date</li>
            <li>Projected COE and final pricing from canonical sales data</li>
          </ul>
        </div>

        {error && (
          <div className="banner warn">
            <p>Error: {error}</p>
          </div>
        )}

        {success && (
          <div className="banner info">
            <p>{success}</p>
          </div>
        )}

        <div className="actions">
          <button
            onClick={generateMylarReport}
            disabled={generating}
            className="btn primary"
          >
            {generating ? 'Generating Report...' : 'Generate Mylar Report'}
          </button>
          <button
            onClick={publishMylarPortal}
            disabled={publishingMylarPortal || !MYLAR_PORTAL_PUBLISH_URL}
            className="btn"
          >
            {publishingMylarPortal ? 'Publishing Secure Portal...' : 'Publish Secure Mylar Portal'}
          </button>
        </div>
      </FormSection>

      <FormSection title="HBFA Monthly Strategic Briefing">
        <p>
          One-month lookback at key market movers, updated daily.
        </p>

        <div className="actions">
          <a
            href={MONTHLY_BRIEFING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn primary"
          >
            Open HBFA Monthly Strategic Briefing
          </a>
        </div>
      </FormSection>

      <FormSection title="OPS Progress Report (PDF)">
        <p>
          Downloads the Aria OPS progress report PDF using the current Daily Checklist
          and units data.
        </p>

        <div style={{ marginBottom: 16 }}>
          <h4>Current Scope:</h4>
          <ul>
            <li>Aria only</li>
            <li>Fixed page groups: 2+1, 5, 3+4, 6</li>
            <li>11x17 portrait PDF download</li>
          </ul>
        </div>

        <div className="actions">
          <button
            onClick={generateOpsProgressReport}
            disabled={generatingOpsProgress || !OPS_PROGRESS_REPORT_URL}
            className="btn primary"
          >
            {generatingOpsProgress ? 'Generating Report...' : 'Generate OPS Progress PDF'}
          </button>
        </div>
      </FormSection>

      <FormSection title="Operations Staff Meeting Agenda (PDF)">
        <OperationsAgendaReport />
      </FormSection>

      <FormSection title="Report Status">
        <div style={{
          padding: 12,
          backgroundColor: '#f4f4f4',
          borderRadius: 4,
          fontSize: 14
        }}>
          <div><strong>Data Sources:</strong></div>
          <div>- Polaris import (writes canonical rows into hbfa_sales_offers)</div>
          <div>- Sales canonical data (hbfa_sales_offers)</div>
          <div>- Daily Checklist latest completed milestone snapshot</div>
          <div style={{ marginTop: 8 }}>
            <strong>Last Generated:</strong> {generating ? 'In progress...' : 'Ready to generate'}
          </div>
        </div>
      </FormSection>
    </div>
  )
}

export default Reports
